import { useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';

import { api } from '@/lib/api';
import { usePaymentConnections } from '@/lib/payment-connection';

export interface PaymentProviderCredentialField {
  key: string;
  labelKey: string;
  helpKey?: string;
  secret: boolean;
  /** When false, the field is optional on save. Omitted means required. */
  required?: boolean;
}

export interface PaymentProviderOnboardingRequirement {
  kind: string;
  titleKey: string;
  bodyKey: string;
  addresses?: string[];
}

export interface PaymentProvider {
  key: string;
  displayName: string;
  supportedMethods: string[];
  supportedCurrencies: string[];
  credentialFields: PaymentProviderCredentialField[];
  onboardingRequirements: PaymentProviderOnboardingRequirement[];
  maxReferenceLength: number;
}

export const translatePaymentProviderKey = (
  key: string,
  tPayments: TFunction<'payments'>,
): string => {
  if (!key.includes(':')) {
    return tPayments(key);
  }

  const [namespace, ...rest] = key.split(':');
  const path = rest.join(':');
  if (namespace === 'payments') {
    return tPayments(path);
  }

  return path;
};

export const usePaymentProviders = (enabled = true) => {
  const query = useQuery({
    queryKey: ['payments', 'providers'],
    queryFn: () => api.get<{ data: PaymentProvider[] }>('/payments/providers'),
    enabled,
  });

  return {
    ...query,
    providers: query.data?.data ?? [],
  };
};

export const useOnlineGivingSetup = (enabled = true) => {
  const providersQuery = usePaymentProviders(enabled);
  const connectionsQuery = usePaymentConnections(enabled);

  const connectionByProvider = new Map(
    connectionsQuery.connections.map((connection) => [connection.provider, connection]),
  );

  return {
    providers: providersQuery.providers,
    isLoading: providersQuery.isLoading || connectionsQuery.isLoading,
    isError: providersQuery.isError || connectionsQuery.isError,
    isReady: connectionsQuery.isReady,
    connections: connectionsQuery.connections,
    connection: connectionsQuery.connection,
    connectionByProvider,
    refetch: async () => {
      await Promise.all([providersQuery.refetch(), connectionsQuery.refetch()]);
    },
  };
};
